import React, { useCallback } from 'react';
import Button from '../../components/Button/Button';
import { PortalWithState } from 'react-portal';
import CancelModal from './CancelModal';
import AssignModal from './AssignModal';
import SetBillingStatusModal from './SetBillingStatusModal';
import useMode from '../../hooks/useMode';
import EmailBackflowTestReportsModal from './EmailBackflowTestReportsModal';
import { getToken } from '../../services/auth';
import SubmitModal from './SubmitModal';
import { JobFilters } from './FilterBarJob';
import useSessionInfo from '../../hooks/useSessionInfo';
import RecreateJobsModal from '../../components/Job/RecreateJobsModal';
import { PREMIUM_COMPANY_IDS } from '../../constants';

type JobsActionsProps = {
  assignmentIds: string[] | readonly string[];
  reload?: () => void;
  filters?: JobFilters;
};

const JobsActions: React.FC<JobsActionsProps> = ({
  assignmentIds,
  reload,
  filters,
}): JSX.Element => {
  const downloadTestReportPdfs = useCallback(() => {
    if (!assignmentIds || assignmentIds.length === 0) {
      return;
    }
    const assignmentIdsPart = `assignmentIds=${assignmentIds.join(',')}`;

    const url = `${
      import.meta.env.ECOS_API_BASE
    }/test-reports/?${assignmentIdsPart}&token=${getToken()}`;
    window.open(url);
  }, [getToken, assignmentIds]);

  const mode = useMode();
  if (mode === 'individual') {
    return <></>;
  }

  const { session } = useSessionInfo();

  const canCancel =
    !filters ||
    (!filters?.status?.includes('CLOSED') &&
      !filters?.status?.includes('SUBMITTED') &&
      !filters?.status?.includes('CANCELED'));

  const haveReports =
    !filters ||
    (!filters?.status?.includes('NEW') &&
      !filters?.status?.includes('ASSIGNED') &&
      !filters?.status?.includes('CANCELED'));

  const canSubmit =
    !filters ||
    (!filters?.status?.includes('NEW') &&
      !filters?.status?.includes('CLOSED') &&
      !filters?.status?.includes('SUBMITTED') &&
      !filters?.status?.includes('CANCELED'));

  return (
    <>
      <PortalWithState closeOnEsc>
        {({ openPortal, closePortal, portal }) => (
          <>
            <Button
              variant="outline"
              disabled={!assignmentIds.length}
              icon="person"
              onClick={openPortal}
            >
              Assign
            </Button>
            {portal(
              <AssignModal
                assignmentIds={assignmentIds}
                onClose={(actionTaken) => {
                  if (actionTaken) {
                    reload?.();
                  }
                  closePortal();
                }}
              />
            )}
          </>
        )}
      </PortalWithState>

      {canCancel && (
        <PortalWithState closeOnEsc>
          {({ openPortal, closePortal, portal }) => (
            <>
              <Button
                variant="outline"
                disabled={!assignmentIds.length}
                icon="slash-circle"
                onClick={openPortal}
              >
                Cancel
              </Button>
              {portal(
                <CancelModal
                  assignmentIds={assignmentIds}
                  onClose={(actionTaken) => {
                    if (actionTaken) {
                      reload?.();
                    }
                    closePortal();
                  }}
                />
              )}
            </>
          )}
        </PortalWithState>
      )}

      {canSubmit && (
        <PortalWithState closeOnEsc>
          {({ openPortal, closePortal, portal }) => (
            <>
              <Button
                variant="outline"
                disabled={!assignmentIds.length}
                icon="check-circle"
                onClick={openPortal}
              >
                Submit
              </Button>
              {portal(
                <SubmitModal
                  assignmentIds={assignmentIds}
                  onClose={(actionTaken) => {
                    if (actionTaken) {
                      reload?.();
                    }
                    closePortal();
                  }}
                />
              )}
            </>
          )}
        </PortalWithState>
      )}

      <PortalWithState closeOnEsc>
        {({ openPortal, closePortal, portal }) => (
          <>
            <Button
              variant="outline"
              disabled={!assignmentIds.length}
              icon="check-circle"
              onClick={openPortal}
            >
              Set Billing Status
            </Button>
            {portal(
              <SetBillingStatusModal
                assignmentIds={assignmentIds}
                onClose={closePortal}
              />
            )}
          </>
        )}
      </PortalWithState>

      {haveReports && (
        <>
          <Button
            variant="outline"
            disabled={!assignmentIds.length}
            icon="file-download"
            onClick={downloadTestReportPdfs}
          >
            Test Report{assignmentIds.length > 1 ? 's' : ''}
          </Button>

          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!assignmentIds.length}
                  icon="mail"
                  onClick={openPortal}
                >
                  Email Test Report{assignmentIds.length > 1 ? 's' : ''}
                </Button>
                {portal(
                  <EmailBackflowTestReportsModal
                    assignmentIds={assignmentIds}
                    onClose={(actionTaken) => {
                      if (actionTaken) {
                        reload?.();
                      }
                      closePortal();
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </>
      )}

      {session?.companyProfile?.id &&
        PREMIUM_COMPANY_IDS.includes(session.companyProfile.id) && (
          <PortalWithState closeOnOutsideClick closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!assignmentIds.length}
                  icon="repeat"
                  onClick={openPortal}
                >
                  Recreate Jobs
                </Button>
                {portal(
                  <RecreateJobsModal
                    inboundAssignmentIds={assignmentIds}
                    onClose={(actionTaken) => {
                      if (actionTaken) {
                        reload?.();
                      }
                      closePortal();
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        )}
    </>
  );
};

export default JobsActions;
